import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { View, TextInput, StyleSheet } from "react-native";
import { observer } from "mobx-react";
import Colors from "../../../Resources/Color";
import { BlockPlanificationConstants } from "../../../Resources/Constants";
import { useDevice } from "../../_layout";

// Component for set value inputs that uses local state for immediate visual feedback
// This pattern matches SerieRowView and works better with MobX observables
//
// iOS Safari auto-zoom prevention (MOBILE ONLY):
// iOS Safari zooms in when focusing inputs with font-size < 16px.
// On mobile, we use font-size: 16px and scale transform to visually shrink text.
// Desktop uses normal font-size (11.5px).
const BASE_FONT_SIZE = 16;
const DESKTOP_FONT_SIZE = 11.5;
const MOBILE_DISPLAY_FONT_SIZE = 11.5;

const SetValueInput = observer(
  ({
    setItem,
    propertyName,
    setInputRefs,
    shouldAutoFocus = false,
    autoFocusKey = null,
    onAutoFocusHandled,
  }) => {
    const { isMobile, isIOS } = useDevice();
    const inputRef = useRef(null);
    const lastAutoFocusKeyRef = useRef(null);

    // Dimensions from constants
    const pillWidth = isMobile
      ? BlockPlanificationConstants.MOBILE_VALUE_PILL_WIDTH
      : BlockPlanificationConstants.VALUE_PILL_WIDTH;
    const pillHeight = isMobile
      ? BlockPlanificationConstants.MOBILE_VALUE_PILL_HEIGHT
      : BlockPlanificationConstants.VALUE_PILL_HEIGHT;

    const [localValue, setLocalValue] = useState(() => {
      if (!setItem || setItem.id === "placeholder") return "";
      return setItem[propertyName] ?? "";
    });

    // Sync local state with MobX observable when it changes externally
    useEffect(() => {
      if (!setItem || setItem.id === "placeholder") {
        if (localValue !== "") setLocalValue("");
        return;
      }
      const mobxValue = setItem[propertyName] ?? "";
      if (mobxValue !== localValue) {
        setLocalValue(mobxValue);
      }
    }, [setItem?.[propertyName], setItem?.id, propertyName, localValue]);

    const handleChange = (text) => {
      setLocalValue(text);
      if (setItem && setItem.id !== "placeholder") {
        setItem.setProperty(propertyName, text);
      }
    };

    const tryAutoFocus = () => {
      if (!shouldAutoFocus) return false;
      if (!autoFocusKey) return false;
      if (!inputRef.current) return false;
      if (lastAutoFocusKeyRef.current === autoFocusKey) return false;
      if (!setItem || setItem.id === "placeholder") return false;
      try {
        inputRef.current.focus?.();
        lastAutoFocusKeyRef.current = autoFocusKey;
        onAutoFocusHandled?.();
        return true;
      } catch (_e) {
        return false;
      }
    };

    // iOS mobile: Use useLayoutEffect for synchronous focus
    useLayoutEffect(() => {
      if (!shouldAutoFocus) {
        lastAutoFocusKeyRef.current = null;
        return;
      }
      // Only use useLayoutEffect for iOS mobile
      if (!isMobile || !isIOS) return;
      tryAutoFocus();
    }, [shouldAutoFocus, autoFocusKey, setItem?.id, isMobile, isIOS]);

    // Desktop/other: Use useEffect with setTimeout
    useEffect(() => {
      if (!shouldAutoFocus) {
        lastAutoFocusKeyRef.current = null;
        return;
      }
      // Skip useLayoutEffect path for iOS mobile (handled above)
      if (isMobile && isIOS) return;
      setTimeout(() => {
        tryAutoFocus();
      }, 0);
    }, [shouldAutoFocus, autoFocusKey, setItem?.id, isMobile, isIOS]);

    const refCallback = (node) => {
      if (!setItem?.id || setItem.id === "placeholder") return;
      const key = `${setItem.id}:${propertyName}`;
      inputRef.current = node || null;
      if (node) setInputRefs.current.set(key, node);
      else setInputRefs.current.delete(key);
      if (node) {
        tryAutoFocus();
      }
    };

    // DESKTOP OR Mobile non-iOS: Original behavior - no scaling, normal font size, border on container
    if (!isMobile || (isMobile && !isIOS)) {
      return (
        <View
          style={[
            styles.valuePill,
            styles.valuePillEditMode,
            { width: pillWidth, height: pillHeight },
          ]}
        >
          <TextInput
            className="serie-pill-input"
            value={localValue}
            editable={setItem?.id !== "placeholder"}
            onChangeText={handleChange}
            placeholder=""
            placeholderTextColor={Colors.grayColor}
            style={styles.valueTextInputDesktop}
            selectTextOnFocus={true}
            ref={refCallback}
            maxLength={11}
          />
        </View>
      );
    }

    // iOS MOBILE: iOS zoom prevention - use 16px font with scale transform
    // Border stays on container at exact pill dimensions from constants
    // TextInput is scaled so text looks like 11.5px but iOS sees 16px
    const textScale = MOBILE_DISPLAY_FONT_SIZE / BASE_FONT_SIZE;
    // Scale width so after transform it matches pillWidth exactly
    const scaledInputWidth = pillWidth / textScale;

    return (
      <View
        style={[
          styles.valuePill,
          styles.valuePillEditMode,
          {
            width: pillWidth,
            height: pillHeight,
            overflow: "hidden",
          },
        ]}
      >
        <TextInput
          className="serie-pill-input"
          value={localValue}
          editable={setItem?.id !== "placeholder"}
          onChangeText={handleChange}
          placeholder=""
          placeholderTextColor={Colors.grayColor}
          style={[
            styles.valueTextInputMobile,
            {
              width: scaledInputWidth,
              transform: [{ scale: textScale }],
            },
          ]}
          selectTextOnFocus={true}
          ref={refCallback}
          maxLength={11}
        />
      </View>
    );
  },
);

const styles = StyleSheet.create({
  // DESKTOP styles - original behavior
  valuePill: {
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  valuePillEditMode: {
    borderWidth: 1,
    borderColor: Colors.borderColor,
  },
  valueTextInputDesktop: {
    fontSize: DESKTOP_FONT_SIZE,
    color: Colors.darkgrayColor,
    fontWeight: "500",
    textAlign: "center",
    backgroundColor: "transparent",
    paddingVertical: 0,
    paddingHorizontal: 0,
    width: "100%",
    height: "100%",
    borderWidth: 0,
    borderRadius: 10,
  },
  // MOBILE styles - uses 16px font with scale transform to prevent iOS auto-zoom
  valueTextInputMobile: {
    fontSize: BASE_FONT_SIZE, // 16px prevents iOS zoom
    color: Colors.darkgrayColor,
    fontWeight: "500",
    textAlign: "center",
    backgroundColor: "transparent",
    paddingVertical: 0,
    paddingHorizontal: 0,
    height: "100%",
    borderWidth: 0,
    // borderRadius: 10,
    outlineStyle: "none",
    outlineWidth: 0,
  },
});

export default SetValueInput;
